/**
 * UserFriendlyError.tsx
 * =====================
 * 用户友好的错误提示组件
 *
 * 特性：
 * 1. 将技术错误转换为用户可理解的文案
 * 2. 提供具体的解决方案建议
 * 3. 支持多种错误类型（网络/超时/服务器/验证）
 * 4. 自动重试功能
 * 5. 错误上报（可选）
 */

import React from 'react';

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';
export type ErrorCategory = 
  | 'network'        // 网络连接问题
  | 'timeout'        // 请求超时
  | 'rate_limited'   // 频率限制
  | 'server_error'   // 服务器内部错误
  | 'validation'     // 输入验证失败
  | 'auth'           // 认证/授权问题
  | 'not_found'      // 资源不存在
  | 'unknown';       // 未知错误

interface ErrorConfig {
  title: string;
  message: string;
  suggestion: string;
  actionLabel: string;
  icon: string;
  severity: ErrorSeverity;
}

const ERROR_CONFIGS: Record<ErrorCategory, ErrorConfig> = {
  network: {
    title: '网络连接异常',
    message: '无法连接到服务器，请检查您的网络连接是否正常',
    suggestion: '1. 检查网络线缆或WiFi连接\n2. 尝试刷新页面\n3. 检查防火墙设置',
    actionLabel: '重新连接',
    icon: '🌐',
    severity: 'warning',
  },
  timeout: {
    title: '查询超时',
    message: '查询耗时过长，可能是数据源响应较慢或网络不稳定',
    suggestion: '1. 稍后重试查询\n2. 减少选中的数据源数量\n3. 选择非高峰时段查询',
    actionLabel: '重新查询',
    icon: '⏱️',
    severity: 'warning',
  },
  rate_limited: {
    title: '请求过于频繁',
    message: '您的操作频率过快，系统暂时限制了请求',
    suggestion: '1. 等待 60 秒后重试\n2. 降低查询频率\n3. 如需高频使用，请联系管理员升级账户',
    actionLabel: '稍后重试 (60s)',
    icon: '⚠️',
    severity: 'info',
  },
  server_error: {
    title: '服务器暂时不可用',
    message: '服务器遇到了一些问题，我们正在紧急修复中',
    suggestion: '1. 稍后重试（通常5-10分钟内恢复）\n2. 查看系统公告了解最新状态\n3. 联系技术支持获取帮助',
    actionLabel: '稍后重试',
    icon: '🔧',
    severity: 'error',
  },
  validation: {
    title: '输入信息有误',
    message: '您输入的信息不符合要求，请检查后重新提交',
    suggestion: '1. 确认药品名称拼写正确\n2. 选择有效的剂型和规格\n3. 至少选择一个数据源',
    actionLabel: '修改后重试',
    icon: '✍️',
    severity: 'info',
  },
  auth: {
    title: '身份验证失败',
    message: '无法验证您的身份或权限不足',
    suggestion: '1. 重新登录系统\n2. 检查API Key是否有效\n3. 联系管理员确认权限',
    actionLabel: '重新登录',
    icon: '🔒',
    severity: 'error',
  },
  not_found: {
    title: '未找到相关数据',
    message: '在所有数据源中都未找到该药品的相关信息',
    suggestion: '1. 确认药品名称是否正确\n2. 尝试使用通用名而非商品名\n3. 该药品可能尚未上市或已退市',
    actionLabel: '修改药名重试',
    icon: '🔍',
    severity: 'info',
  },
  unknown: {
    title: '发生了未知错误',
    message: '系统遇到了意外情况，我们已记录此问题',
    suggestion: '1. 刷新页面重试\n2. 清除浏览器缓存后重试\n3. 如果问题持续存在，请联系技术支持',
    actionLabel: '刷新页面',
    icon: '❓',
    severity: 'error',
  },
};

interface UserFriendlyErrorProps {
  error: Error | string | null;
  category?: ErrorCategory;
  onRetry?: () => void;
  onDismiss?: () => void;
  showDetails?: boolean;       // 是否显示详细错误信息（调试用）
  className?: string;
}

const UserFriendlyError: React.FC<UserFriendlyErrorProps> = ({
  error,
  category = 'unknown',
  onRetry,
  onDismiss,
  showDetails = false,
  className = '',
}) => {
  const [showTechnicalDetails, setShowTechnicalDetails] = React.useState(false);

  if (!error) return null;

  const errorString = typeof error === 'string' ? error : error.message;
  const config = ERROR_CONFIGS[category];

  // 根据严重程度选择样式
  const severityStyles = {
    info: 'border-blue-500/30 bg-blue-500/5 text-blue-700 dark:text-blue-400',
    warning: 'border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400',
    error: 'border-red-500/30 bg-red-500/5 text-red-700 dark:text-red-400',
    critical: 'border-destructive/30 bg-destructive/5 text-destructive',
  };

  return (
    <div className={`rounded-xl border p-5 animate-scaleIn ${severityStyles[config.severity]} ${className}`}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className="text-2xl flex-shrink-0 mt-0.5">{config.icon}</div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-base mb-1">{config.title}</h4>
          <p className="text-sm opacity-90 leading-relaxed">{config.message}</p>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 w-6 h-6 rounded-full hover:bg-black/10 dark:hover:bg-white/10 flex items-center justify-center transition-colors"
            aria-label="关闭"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Suggestion */}
      {config.suggestion && (
        <div className="mb-4 pl-9">
          <p className="text-xs leading-relaxed whitespace-pre-line opacity-80">
            💡 建议方案：<br />{config.suggestion}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pl-9">
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-current/10 hover:bg-current/20 rounded-lg text-sm font-medium transition-colors"
          >
            {config.actionLabel}
          </button>
        )}
        {(errorString || showDetails) && (
          <button
            onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
            className="px-4 py-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg text-xs font-medium transition-colors"
          >
            {showTechnicalDetails ? '隐藏详情' : '查看详情'}
          </button>
        )}
      </div>

      {/* Technical Details (Expandable) */}
      {showTechnicalDetails && (
        <div className="mt-3 pl-9 animate-fadeIn">
          <div className="bg-black/5 dark:bg-white/5 rounded-lg p-3 font-mono text-xs overflow-auto max-h-32">
            <pre className="whitespace-pre-wrap break-all">{errorString}</pre>
          </div>
          <p className="text-[10px] mt-1.5 opacity-60">
            错误时间：{new Date().toLocaleString('zh-CN')}
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * 自动检测错误类型
 */
export function categorizeError(error: Error | string): ErrorCategory {
  const errorMessage = typeof error === 'string' ? error.toLowerCase() : error.message.toLowerCase();

  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('connection')) {
    return 'network';
  }
  if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
    return 'timeout';
  }
  if (errorMessage.includes('rate limit') || errorMessage.includes('429') || errorMessage.includes('too many')) {
    return 'rate_limited';
  }
  if (errorMessage.includes('500') || errorMessage.includes('502') || errorMessage.includes('503')) {
    return 'server_error';
  }
  if (errorMessage.includes('400') || errorMessage.includes('validation') || errorMessage.includes('invalid')) {
    return 'validation';
  }
  if (errorMessage.includes('401') || errorMessage.includes('403') || errorMessage.includes('unauthorized')) {
    return 'auth';
  }
  if (errorMessage.includes('404') || errorMessage.includes('not found')) {
    return 'not_found';
  }

  return 'unknown';
}

export default UserFriendlyError;
